<template>
  <div class="option-card-container">
    <!-- Option Info Section -->
    <div class="option-info">
      <div class="option-number">{{ index + 1 }}</div>
      <div class="option-details">
        <ModalEditarDescricaoOpcaoEnquete
          :editFormData="opcao"
          :enquete="enquete"
          @option-updated="onOptionUpdated"
        />
      </div>
      <div class="option-result">
        <span class="result-value">{{ opcao.resultado ?? 0 }}</span>
        <span class="result-percent">%</span>
      </div>
    </div>

    <!-- Actions Section -->
    <div class="option-actions">
      <!-- Color Picker -->
      <v-menu
        :close-on-content-click="false"
        transition="scale-transition"
        v-model="isColorPickerActive"
        offset-y
      >
        <template #activator="{ on, attrs }">
          <div
            class="action-box color-box"
            :class="{ 'no-color': !hasCustomColor }"
            v-bind="attrs"
            v-on="on"
          >
            <template v-if="hasCustomColor">
              <div class="color-preview" :style="{ backgroundColor: optionColor.hex }"></div>
            </template>
            <template v-else>
              <v-icon size="24" color="text_secondary">mdi-palette-outline</v-icon>
            </template>
            <span class="action-label">Cor barra</span>
          </div>
        </template>
        <v-card class="color-picker-card">
          <v-card-text class="pa-3">
            <v-color-picker
              v-model="optionColor"
              dot-size="20"
              :value="optionColor.hex"
              swatches-max-height="200"
              hide-inputs
              mode="hexa"
            />
            <div class="color-preview-bar mt-3" :style="{ backgroundColor: optionColor.hex }">
              {{ optionColor.hex }}
            </div>
            <div class="color-actions mt-3">
              <v-btn
                outlined
                small
                class="reset-btn"
                @click="resetColor"
                :loading="isResettingColor"
                :disabled="isUpdatingColor || !hasCustomColor"
              >
                <v-icon small left>mdi-refresh</v-icon>
                Resetar
              </v-btn>
              <v-btn
                color="primary"
                small
                class="save-btn"
                @click="updateColor"
                :loading="isUpdatingColor"
                :disabled="isResettingColor"
              >
                Salvar
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-menu>

      <!-- Image Upload -->
      <div class="action-box image-box" @click="triggerImageUpload">
        <template v-if="urlImagemFile">
          <v-img
            class="image-preview"
            :src="urlImagemFile"
            @error="onImageError"
          >
            <div class="image-overlay">
              <v-btn
                icon
                small
                class="overlay-btn"
                @click.stop="removeImage"
                :loading="isRemovingImage"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                class="overlay-btn"
                @click.stop="triggerImageUpload"
                :loading="isUploading"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </v-img>
        </template>
        <template v-else>
          <v-icon size="24" color="text_secondary">mdi-image-plus</v-icon>
          <span class="action-label">Imagem</span>
        </template>
        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onFileInputChange"
        />
      </div>

      <!-- Shield/Escudo (only for Futebol) -->
      <template v-if="isFutebol">
        <div
          class="action-box shield-box"
          @click="opcao.metadados_sde_equipe ? null : openSdeModal(opcao)"
          :class="{ 'has-shield': opcao.metadados_sde_equipe }"
        >
          <template v-if="opcao.metadados_sde_equipe">
            <v-img
              class="shield-preview"
              :src="opcao.metadados_sde_equipe.escudo_img_url"
              contain
            >
              <div class="image-overlay">
                <v-btn
                  icon
                  small
                  class="overlay-btn"
                  @click.stop="limparAssociacao"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </v-img>
          </template>
          <template v-else>
            <v-icon size="24" color="text_secondary">mdi-shield-plus</v-icon>
            <span class="action-label">Escudo</span>
          </template>
        </div>
      </template>
    </div>

    <!-- SDE Modal -->
    <v-dialog
      v-model="showSdeModal"
      max-width="500"
      content-class="sc-modal sc-modal--sm"
    >
      <v-card v-if="showSdeModal" flat class="sc-modal__card">
        <v-btn icon class="sc-modal__close" @click="showSdeModal = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <!-- Header -->
        <div class="sc-modal__header">
          <v-icon color="primary" class="mr-3">mdi-shield-search</v-icon>
          <div>
            <span class="sc-modal__title">Associar equipe</span>
            <span class="sc-modal__subtitle">Escolha o campeonato e o clube</span>
          </div>
        </div>

        <!-- Body -->
        <div class="sc-modal__body">
          <div class="sc-modal__field">
            <label class="sc-modal__field-label">Campeonato</label>
            <v-select
              solo
              flat
              dense
              background-color="surface_tertiary"
              :items="campeonatos"
              item-text="dsc_nome_campeonato"
              return-object
              v-model="selectedCampeonato"
              placeholder="Selecione o campeonato"
              hide-details="auto"
              @change="fetchEquipes"
            />
          </div>

          <div v-if="equipes.length" class="sc-modal__field">
            <label class="sc-modal__field-label">Equipe</label>
            <div class="equipes-list">
              <button
                v-for="equipe in equipes"
                :key="equipe.equipe_id"
                type="button"
                class="equipe-item"
                :class="{ 'equipe-item--selected': selectedEquipe?.equipe_id === equipe.equipe_id }"
                @click="selectedEquipe = equipe"
              >
                <v-avatar size="36" class="mr-3">
                  <v-img :src="equipe.escudos.svg" />
                </v-avatar>
                <div class="equipe-info">
                  <span class="equipe-name">{{ equipe.nome_popular }}</span>
                  <span class="equipe-full-name">{{ equipe.nome }}</span>
                </div>
                <v-icon v-if="selectedEquipe?.equipe_id === equipe.equipe_id" color="primary" class="ml-auto">
                  mdi-check-circle
                </v-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="sc-modal__footer">
          <v-btn text @click="showSdeModal = false" class="modal-btn-cancel">Cancelar</v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedEquipe"
            @click="confirmarAssociacao"
            class="modal-btn-confirm"
          >
            <v-icon small class="mr-1">mdi-check</v-icon>
            Selecionar
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ModalEditarDescricaoOpcaoEnquete from "@/components/Modal/ConfiguracoesProducao/ModalEditarDescricaoOpcaoEnquete.vue";
import { apiClient } from "@/services/api";
import { mapActions } from "vuex";

export default {
  components: { ModalEditarDescricaoOpcaoEnquete },
  props: {
    enquete: Object,
    opcao: Object,
    index: Number,
    aprovadas_current_page: Number,
  },

  data() {
    return {
      optionColor: { hex: "#000000" },
      imageFile: null,
      urlImagemFile: null,
      dsc_arquivo_gcp: null,
      isColorPickerActive: false,
      isUploading: false,
      isRemovingImage: false,
      isUpdatingColor: false,
      isResettingColor: false,
      showSdeModal: false,
      campeonatos: [],
      equipes: [],
      selectedCampeonato: null,
      selectedEquipe: null,
      currentOpcao: null,
    };
  },

  computed: {
    isFutebol() {
      return this.enquete?.categoria?.dsc_nome_subcategoria?.toUpperCase() === "FUTEBOL";
    },
    hasCustomColor() {
      return !!this.opcao.dsc_cor_barra;
    },
  },

  created() {
    this.optionColor.hex = this.opcao.dsc_cor_barra || "#000000";
    this.urlImagemFile = this.opcao.url_thumbnail;
    this.dsc_arquivo_gcp = this.opcao.dsc_nome_arquivo;
  },

  watch: {
    "opcao.dsc_cor_barra": {
      handler(newValue) {
        this.optionColor.hex = newValue || "#000000";
      },
    },
    "opcao.url_thumbnail": {
      handler(newValue) {
        if (this.urlImagemFile && this.urlImagemFile.startsWith("blob:")) {
          URL.revokeObjectURL(this.urlImagemFile);
        }
        this.urlImagemFile = newValue;
      },
    },
    "opcao.dsc_nome_arquivo": {
      handler(newValue) {
        this.dsc_arquivo_gcp = newValue;
      },
    },
  },

  beforeDestroy() {
    if (this.urlImagemFile && this.urlImagemFile.startsWith("blob:")) {
      URL.revokeObjectURL(this.urlImagemFile);
    }
  },

  methods: {
    ...mapActions({
      getEnquetesProducao: "enquetes/getEnquetesProducao",
      getEnqueteEmExibicao: "enquetes/getEnqueteEmExibicao",
    }),

    triggerImageUpload() {
      this.$refs.imageInput?.click();
    },
    onFileInputChange(event) {
      const file = event.target.files?.[0];
      if (file) {
        this.onChange(file);
      }
      event.target.value = '';
    },
    openSdeModal(opcao) {
      this.currentOpcao = opcao;
      this.fetchCampeonatos();
      this.showSdeModal = true;
    },
    transformFileNameToUniqueName(fileName) {
      const data_hoje = new Date();
      const miliseconds = data_hoje.getMilliseconds();
      const data_formatada = data_hoje
        .toLocaleString("pt-BR", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
        })
        .replaceAll(",", "")
        .replaceAll("/", "")
        .replaceAll(":", "")
        .replaceAll(" ", "")
        .concat("-", String(miliseconds))
        .concat("-", fileName);

      return data_formatada;
    },
    async fetchCampeonatos() {
      try {
        const res = await apiClient.get("/integracoes/sde/campeonatos");
        this.campeonatos = res.data;
      } catch (e) {
        console.error(e);
        this.$toast.error("Erro ao buscar campeonatos");
      }
    },
    async fetchEquipes() {
      if (!this.selectedCampeonato) return;
      const { dsc_slug_campeonato, dsc_slug_campeonato_edicao } = this.selectedCampeonato;
      try {
        const { data } = await apiClient.get("/integracoes/sde/clubes", {
          params: {
            dsc_slug_campeonato,
            dsc_slug_campeonato_edicao,
          },
        });
        this.equipes = data;
      } catch (e) {
        console.error(e);
        this.$toast.error("Erro ao buscar equipes");
      }
    },
    async confirmarAssociacao() {
      if (!this.selectedEquipe) return;
      try {
        const { cod_producao } = this.$store.state.producoes.selectedProducao;

        await apiClient.post(
          `/producao/${cod_producao}/enquetes/${this.opcao.id_enquete}/opcoes/${this.opcao.id}/metadados-sde`,
          {
            equipe_id: this.selectedEquipe.equipe_id,
            escudo_img_url: this.selectedEquipe.escudos.svg,
            dsc_nome_popular: this.selectedEquipe.nome_popular,
          }
        );
        this.$toast.success("Associação realizada com sucesso!");
        this.showSdeModal = false;
        await this.refreshEnquetes();
      } catch (e) {
        console.error(e);
        this.$toast.error("Erro ao associar equipe");
      }
    },
    async limparAssociacao() {
      try {
        const { cod_producao } = this.$store.state.producoes.selectedProducao;

        await apiClient.delete(
          `/producao/${cod_producao}/enquetes/${this.opcao.id_enquete}/opcoes/${this.opcao.id}/metadados-sde`
        );
        this.$toast.success("Associação removida");
        await this.refreshEnquetes();
      } catch (e) {
        console.error(e);
        this.$toast.error("Erro ao remover associação");
      }
    },
    async refreshEnquetes() {
      const selectedProducao = this.$store.state.producoes.selectedProducao;
      if (!selectedProducao?.cod_producao) {
        console.error("No production selected for refresh");
        return;
      }

      await this.getEnquetesProducao({
        cod_producao: selectedProducao.cod_producao,
        filtro_texto: this.$store.state.enquetes.filtroEnquetes,
        size: 5,
        page: this.aprovadas_current_page,
        cod_portal: this.$store.state.enquetes.selectedPortal,
        id_categoria: this.$store.state.enquetes.selectedCategoria,
      });
      await this.getEnqueteEmExibicao({
        cod_producao: selectedProducao.cod_producao,
      });
    },

    async onChange(file) {
      if (this.isUploading) return;

      if (!file) return;
      this.imageFile = file;

      const file_type = this.imageFile.type.split("/");
      if (!file_type[1]) {
        this.$toast.error("Tipo de arquivo inválido");
        return;
      }

      const selectedProducao = this.$store.state.producoes.selectedProducao;
      if (!selectedProducao?.cod_producao) {
        this.$toast.error("Nenhuma produção selecionada");
        return;
      }

      if (this.urlImagemFile && this.urlImagemFile.startsWith("blob:")) {
        URL.revokeObjectURL(this.urlImagemFile);
      }
      this.urlImagemFile = URL.createObjectURL(this.imageFile);

      const fileName = (this.opcao.chave_backstage || `option_${this.opcao.id}`).concat(".", file_type[1])

      this.dsc_arquivo_gcp = this.transformFileNameToUniqueName(fileName);

      this.isUploading = true;
      try {
        await apiClient.post(
          "uploadFileToGCP",
          {
            file: this.imageFile,
            subpasta: "enquetes",
            fileName: this.dsc_arquivo_gcp,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const response = await apiClient.get(
          `getBucketImages/?fileName=${encodeURIComponent(this.dsc_arquivo_gcp)}&subpasta=enquetes`
        );

        if (!response.data) {
          throw new Error("Failed to get uploaded image URL");
        }

        await apiClient.patch(
          `/producao/${selectedProducao.cod_producao}/enquetes/${this.opcao.id_enquete}/opcoes/${this.opcao.id}`,
          {
            dsc_titulo: this.opcao.dsc_titulo,
            url_imagem: response.data.url,
            url_thumbnail: response.data.url,
            dsc_nome_arquivo: this.dsc_arquivo_gcp,
            dsc_cor_barra: this.opcao.dsc_cor_barra,
          }
        );

        await this.refreshEnquetes();
        this.$toast.success("Imagem adicionada com sucesso");
      } catch (error) {
        console.error("Error uploading image:", error);
        this.$toast.error("Erro ao adicionar imagem");
        if (this.urlImagemFile && this.urlImagemFile.startsWith("blob:")) {
          URL.revokeObjectURL(this.urlImagemFile);
        }
        this.urlImagemFile = null;
        this.imageFile = null;
      } finally {
        this.isUploading = false;
      }
    },
    onImageError() {
      if (this.urlImagemFile && this.urlImagemFile.startsWith("blob:")) {
        URL.revokeObjectURL(this.urlImagemFile);
      }
      this.urlImagemFile = null;
      this.dsc_arquivo_gcp = null;
    },
    async removeImage() {
      if (this.isRemovingImage) return;

      const selectedProducao = this.$store.state.producoes.selectedProducao;
      if (!selectedProducao?.cod_producao) {
        this.$toast.error("Nenhuma produção selecionada");
        return;
      }

      this.isRemovingImage = true;
      try {
        await apiClient.delete(
          `deleteBucketImage/?fileName=${encodeURIComponent(this.dsc_arquivo_gcp)}&subpasta=enquetes`
        );

        this.imageFile = null;
        if (this.urlImagemFile && this.urlImagemFile.startsWith("blob:")) {
          URL.revokeObjectURL(this.urlImagemFile);
        }
        this.urlImagemFile = null;
        this.dsc_arquivo_gcp = null;

        await apiClient.patch(
          `/producao/${selectedProducao.cod_producao}/enquetes/${this.opcao.id_enquete}/opcoes/${this.opcao.id}`,
          {
            dsc_titulo: this.opcao.dsc_titulo,
            url_imagem: null,
            url_thumbnail: null,
            dsc_nome_arquivo: null,
            dsc_cor_barra: this.opcao.dsc_cor_barra,
          }
        );

        await this.refreshEnquetes();
        this.$toast.success("Imagem removida com sucesso");
      } catch (error) {
        console.error("Error removing image:", error);
        this.$toast.error("Erro ao remover imagem");
      } finally {
        this.isRemovingImage = false;
      }
    },
    onOptionUpdated() {
      this.$emit("option-updated");
    },
    async updateColor() {
      if (this.isUpdatingColor) return;

      const selectedProducao = this.$store.state.producoes.selectedProducao;
      if (!selectedProducao?.cod_producao) {
        this.$toast.error("Nenhuma produção selecionada");
        return;
      }

      this.isUpdatingColor = true;
      try {
        await apiClient.patch(
          `/producao/${selectedProducao.cod_producao}/enquetes/${this.opcao.id_enquete}/opcoes/${this.opcao.id}`,
          {
            dsc_titulo: this.opcao.dsc_titulo,
            url_imagem: this.opcao.url_imagem,
            url_thumbnail: this.opcao.url_thumbnail,
            dsc_nome_arquivo: this.opcao.dsc_nome_arquivo,
            dsc_cor_barra: this.optionColor.hex,
          }
        );

        await this.refreshEnquetes();
        this.$toast.success("Cor atualizada com sucesso.");
        this.isColorPickerActive = false;
      } catch (error) {
        console.error("Error updating color:", error);
        this.$toast.error("Erro ao atualizar cor");
      } finally {
        this.isUpdatingColor = false;
      }
    },
    async resetColor() {
      if (this.isResettingColor) return;

      const selectedProducao = this.$store.state.producoes.selectedProducao;
      if (!selectedProducao?.cod_producao) {
        this.$toast.error("Nenhuma produção selecionada");
        return;
      }

      this.isResettingColor = true;
      try {
        await apiClient.patch(
          `/producao/${selectedProducao.cod_producao}/enquetes/${this.opcao.id_enquete}/opcoes/${this.opcao.id}`,
          {
            dsc_titulo: this.opcao.dsc_titulo,
            url_imagem: this.opcao.url_imagem,
            url_thumbnail: this.opcao.url_thumbnail,
            dsc_nome_arquivo: this.opcao.dsc_nome_arquivo,
            dsc_cor_barra: null,
          }
        );

        await this.refreshEnquetes();
        this.$toast.success("Cor resetada com sucesso.");
        this.isColorPickerActive = false;
      } catch (error) {
        console.error("Error resetting color:", error);
        this.$toast.error("Erro ao resetar cor");
      } finally {
        this.isResettingColor = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.option-card-container {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 16px;
}

/* Option Info */
.option-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.option-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: var(--v-surface_primary-base);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.option-details {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.option-result {
  display: flex;
  align-items: baseline;
  gap: 2px;
  padding: 6px 12px;
  background: var(--sc-surface-tint-hover);
  border-radius: var(--sc-radius-md);
  flex-shrink: 0;
}

.result-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
}

.result-percent {
  font-size: 0.75rem;
  color: var(--v-text_secondary-base);
}

/* Actions Section */
.option-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}


/* Color Box */
.color-box {
  border-style: solid;

  &.no-color {
    border-style: dashed;
  }
}

.color-preview {
  width: 28px;
  height: 28px;
  border-radius: var(--sc-radius-sm);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}


.color-picker-card {
  border-radius: var(--sc-radius-lg) !important;
}

.color-preview-bar {
  height: 32px;
  border-radius: var(--sc-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.color-actions {
  display: flex;
  gap: 8px;

  .reset-btn {
    flex: 1;
  }

  .save-btn {
    flex: 1;
  }
}

/* Image Box */
.image-box {
  position: relative;
}

.image-preview {
  width: 100%;
  height: 100%;
  border-radius: 9px;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 60%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 6px;
  gap: 6px;
  opacity: 0;
  border-radius: 9px;
}

.image-box:hover .image-overlay,
.shield-box:hover .image-overlay {
  opacity: 1;
}

.overlay-btn {
  background: var(--sc-surface-tint-stronger) !important;
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.3) !important;
  }
}

/* Shield Box */
.shield-box {
  position: relative;

  &.has-shield {
    border-style: solid;
    border-color: transparent;
    background: var(--sc-surface-tint-hover);
  }
}

.shield-preview {
  width: 40px;
  height: 40px;
}

/* SDE — equipes picker (component-specific extension only) */
.equipes-list {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.equipe-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  border-radius: var(--sc-radius-md);
  background: var(--sc-surface-tint);
  border: 1px solid transparent;
  text-align: left;
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard),
              border-color var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-surface-tint-hover);
    border-color: var(--sc-border-medium);
  }

  &--selected {
    background: rgba(var(--sc-color-primary-rgb), 0.12);
    border-color: var(--v-primary-base);
  }
}

.equipe-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.equipe-name {
  font-size: var(--sc-text-base);
  font-weight: var(--sc-weight-medium);
  color: var(--v-text_primary-base);
}

.equipe-full-name {
  font-size: var(--sc-text-md);
  color: var(--v-text_secondary-base);
}
</style>
